import React, { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import i18n from "../i18n";

import { setPromoColors } from "./utils";
import { LOBBY_CONFIG_DEFAULTS } from "./lobbyConfigDefaults";
import { isSweepOrg } from "../utils/sweepUtils";
import { isNoGradientOrg, isSoccerbetOrg } from "../utils/brandUtils";
import { getFontUrl } from "../utils/fontUrl";
import httpClient from "@/api/httpClient.ts";

import PageNotFound from "../components/pageNotFound/PageNotFound";
import PageSkeleton from "@/components/skeleton/PageSkeleton";

const LobbyConfigContext = createContext<any>(null);

const setColorVariables = (paramKey: string, paramValue: string) => {
  document.documentElement.style.setProperty(`${paramKey}`, `${paramValue}`);
};

const setLobbyBackground = (bgString: string | undefined) => {
  if (!bgString || bgString.length === 0) {
    return;
  }
  const bgObj = JSON.parse(bgString);
  let desktopUri = bgObj?.["desktop"]?.[0]?.uri;
  let mobileUri = bgObj?.["mobile"]?.[0]?.uri;

  if (!desktopUri && !mobileUri) {
    const enUri = bgObj?.["en"]?.[0]?.uri;
    if (enUri) {
      desktopUri = enUri;
      mobileUri = enUri;
    }
  }
  if (desktopUri) {
    setColorVariables("--background-image-web-lobby", `url(${desktopUri})`);
    setColorVariables("--background-image-web", `url(${desktopUri})`);
  }
  if (mobileUri) {
    setColorVariables("--background-image-mobile-lobby", `url(${mobileUri})`);
    setColorVariables("--background-image-mobile", `url(${mobileUri})`);
  }
};

const getColorVariable = (varName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName);
};
const updateFontVariables = (fontWeight: string, fontName: string) => {
  const currentFonts = getColorVariable(`--font-${fontWeight}`);
  const newFontFace = `${fontName}, ${currentFonts}`;
  document.documentElement.style.setProperty(`--font-${fontWeight}`, newFontFace);
};
const FONT_FORMATS: Record<string, string> = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};

const getFontFormat = (fontPath: string) => {
  const ext = fontPath?.split("?")?.[0]?.split(".")?.pop()?.toLowerCase() || "";
  const format = FONT_FORMATS[ext];
  return format ? ` format('${format}')` : "";
};

function createFontFaceStyle(fontPath: string, fontWeight: string) {
  const fontName = (fontPath as any)?.split("/")?.pop()?.split(".")?.[0];
  const fontFaceStyleElement = document.createElement("style");
  fontFaceStyleElement.innerHTML = `
    @font-face {
        font-family: ${fontName};
        font-weight: ${fontWeight?.toLowerCase()};
        font-display: swap;
        src: url('${getFontUrl(fontPath)}')${getFontFormat(fontPath)};
      }
    `;
  document.head.appendChild(fontFaceStyleElement);
  updateFontVariables(fontWeight?.toLowerCase(), fontName);
}

export const LobbyConfigProvider = ({
  dataRoute,
  children,
  loadingFallback,
  errorFallback,
}: {
  children: React.ReactNode;
  dataRoute: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}) => {
  const {
    data: lobbyConfig,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["lobbyConfig", dataRoute],
    queryFn: () =>
      httpClient.get(dataRoute).then((response) => {
        const fallback = LOBBY_CONFIG_DEFAULTS[dataRoute];
        return fallback && typeof response.data === "string" ? { data: fallback } : response;
      }),
    select: (lobbyConfig) => lobbyConfig.data,
  });

  if (isError) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }
  if (isLoading) {
    return <>{loadingFallback ?? <PageSkeleton />}</>;
  }

  if (!lobbyConfig) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }

  const layout = (() => {
    try {
      return JSON.parse(lobbyConfig.layout);
    } catch (error) {
      return null;
    }
  })();

  if (!layout) {
    return <PageNotFound />;
  }

  const loadFonts = async () => {
    if (!lobbyConfig.fonts) return;

    try {
      const fonts = JSON.parse(lobbyConfig.fonts);
      if (!Array.isArray(fonts) || fonts.length === 0) return;

      const lang = i18n.language;
      const selectedLang =
        fonts.find((font: any) => font.lang === lang) ||
        fonts.find((font: any) => font.default) ||
        fonts[0];

      if (selectedLang && selectedLang.fontFamily) {
        Object.entries(selectedLang.fontFamily).forEach(([fontWeight, fontPath]) => {
          if (typeof fontPath === "string") {
            createFontFaceStyle(fontPath, fontWeight);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load fonts:", error);
    }
  };

  loadFonts();
  setPromoColors(layout);
  setLobbyBackground(lobbyConfig.background);
  document.documentElement.classList.toggle("no-gradients", isNoGradientOrg(lobbyConfig?.orgUuid));

  const contextValue = {
    ...lobbyConfig,
    isSweep: isSweepOrg(lobbyConfig?.orgUuid),
    isSoccerbet: isSoccerbetOrg(lobbyConfig?.orgUuid),
  };

  return <LobbyConfigContext.Provider value={contextValue}>{children}</LobbyConfigContext.Provider>;
};

export const useLobbyConfig = () => {
  return useContext(LobbyConfigContext);
};

export { LobbyConfigContext };
